if (!customElements.get('atlas-product')) {
  customElements.define('atlas-product', class extends HTMLElement {
    connectedCallback() {
      const section = this;
      const wrapper = section.closest('.atlas-product__wrapper');
      if (!wrapper) return;

      /** Sync floating ATC visibility after variant / disabled state changes */
      let syncFloatingUi = () => {};

      const form = section.querySelector('form');
      if (!form) return;
      const variantsEl = wrapper.parentNode.querySelector('[data-atlas-product-variants]');
      const mediaItemsEl = wrapper.parentNode.querySelector('[data-atlas-product-media]');
      const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
      const mediaItems = mediaItemsEl ? JSON.parse(mediaItemsEl.textContent) : [];
      const optionInputs = Array.from(form.querySelectorAll('[data-option-position]'));
      const variantIdInput = section.querySelector('[data-variant-id]');
      const priceEl = section.querySelector('[data-price]');
      const stockEl = section.querySelector('[data-stock-warning]');
      const cartBtn = section.querySelector('[data-cart-btn]');
      const cartBtnText = section.querySelector('[data-cart-btn-text]');
      const selectedColorEl = section.querySelector('[data-selected-color]');
      const galleryMain = wrapper.querySelector('[data-gallery-main]');
      const galleryThumbs = wrapper.querySelector('[data-gallery-thumbs]');
      const galleryThumbsOuter = wrapper.querySelector('[data-gallery-thumbs-outer]');
      const getGalleryItems = () => Array.from(wrapper.querySelectorAll('.atlas-product__gallery-item'));
      const getThumbs = () => Array.from(wrapper.querySelectorAll('[data-thumb]'));
      const lowStockOne = section.dataset.lowStockOne;
      const lowStockOther = section.dataset.lowStockOther;
      const addToCartText = section.dataset.addToCartText;
      const soldOutText = section.dataset.soldOutText;
      const unavailableText = section.dataset.unavailableText;

      function getSelectedOptions() {
        const selected = [];

        optionInputs.forEach((input) => {
          if (input.checked) {
            selected[Number(input.dataset.optionPosition) - 1] = input.value;
          }
        });

        return selected;
      }

      function variantMatchesOptions(variant, options) {
        return options.every((value, index) => !value || variant.options[index] === value);
      }

      function findExactVariant(options) {
        return variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      function findFirstMatchingVariant(options) {
        return variants.find((variant) => variant.available && variantMatchesOptions(variant, options));
      }

      function syncInputsToVariant(variant) {
        if (!variant) return;

        optionInputs.forEach((input) => {
          const position = Number(input.dataset.optionPosition) - 1;
          input.checked = variant.options[position] === input.value;
        });
      }

      function updateOptionAvailability() {
        const selected = getSelectedOptions();

        optionInputs.forEach((input) => {
          const position = Number(input.dataset.optionPosition) - 1;
          const candidate = selected.slice();
          candidate[position] = input.value;

          const available = variants.some((variant) => variant.available && variantMatchesOptions(variant, candidate));
          input.disabled = !available;

          const label = input.nextElementSibling;
          if (!label) return;

          if (input.classList.contains('atlas-product__size-input')) {
            label.classList.toggle('atlas-product__size--unavailable', !available);
          }

          if (input.classList.contains('atlas-product__swatch-input')) {
            label.classList.toggle('atlas-product__swatch--unavailable', !available);
          }
        });
      }

      function getLowStockText(quantity) {
        const template = quantity === 1 ? lowStockOne : lowStockOther;
        return template.replace('[count]', String(quantity));
      }

      let galleryNavLock = false;
      let galleryNavLockTimer = null;
      let galleryScrollSyncTimer = null;
      let thumbsSyncRaf = null;
      let thumbsUserScrolling = false;
      let thumbsSyncing = false;
      let thumbsDidDrag = false;
      let thumbsUserScrollTimer = null;
      let thumbsPointer = null;
      let activeMediaId = null;

      function getScrollPaddingLeft() {
        if (!galleryMain) return 0;
        return parseFloat(getComputedStyle(galleryMain).scrollPaddingLeft) || 0;
      }

      function getActiveGalleryIndex() {
        const galleryItems = getGalleryItems();
        if (!galleryMain || !galleryItems.length) return 0;

        const snapEdge = galleryMain.scrollLeft + getScrollPaddingLeft();
        let closestIndex = 0;
        let closestDistance = Infinity;

        galleryItems.forEach((item, index) => {
          const distance = Math.abs(item.offsetLeft - snapEdge);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      function getActiveMediaId() {
        const galleryItems = getGalleryItems();
        const index = getActiveGalleryIndex();
        return galleryItems[index]?.dataset.mediaId || galleryItems[0]?.dataset.mediaId;
      }

      function lockGalleryNavigation() {
        galleryNavLock = true;
        clearTimeout(galleryNavLockTimer);
        galleryNavLockTimer = window.setTimeout(() => {
          galleryNavLock = false;
          syncActiveMediaFromScroll();
        }, 450);
      }

      function syncActiveMediaFromScroll() {
        const mediaId = getActiveMediaId();
        if (mediaId) setActiveMedia(mediaId);
      }

      function updateGallery(mediaId) {
        if (!mediaId || !galleryMain) return;

        const galleryItems = getGalleryItems();
        const target = galleryItems.find((item) => String(item.dataset.mediaId) === String(mediaId));
        if (!target) return;

        lockGalleryNavigation();
        galleryMain.scrollTo({
          left: Math.max(0, target.offsetLeft - getScrollPaddingLeft()),
          behavior: 'smooth',
        });

        setActiveMedia(mediaId);
      }

      function getGalleryFractionalIndex() {
        const galleryItems = getGalleryItems();
        if (!galleryMain || galleryItems.length <= 1) return 0;

        const snapEdge = galleryMain.scrollLeft + getScrollPaddingLeft();
        const firstLeft = galleryItems[0].offsetLeft;
        if (snapEdge <= firstLeft) return 0;

        for (let index = 0; index < galleryItems.length - 1; index++) {
          const currentLeft = galleryItems[index].offsetLeft;
          const nextLeft = galleryItems[index + 1].offsetLeft;
          if (snapEdge <= nextLeft) {
            const span = nextLeft - currentLeft;
            if (span <= 0) return index;
            return index + Math.min(1, Math.max(0, (snapEdge - currentLeft) / span));
          }
        }

        return galleryItems.length - 1;
      }

      function getThumbStep() {
        const thumbs = getThumbs();
        if (thumbs.length < 2) return thumbs[0]?.offsetWidth || 0;
        return thumbs[1].offsetLeft - thumbs[0].offsetLeft;
      }

      function markThumbsUserScroll() {
        if (thumbsSyncing) return;
        thumbsUserScrolling = true;
        clearTimeout(thumbsUserScrollTimer);
        thumbsUserScrollTimer = window.setTimeout(() => {
          thumbsUserScrolling = false;
        }, 180);
      }

      function getThumbsViewport() {
        return galleryThumbsOuter || galleryThumbs?.parentElement;
      }

      function syncThumbsToGallery() {
        const viewport = getThumbsViewport();
        if (!galleryThumbs || !viewport) return;
        if (thumbsUserScrolling) return;

        const thumbs = getThumbs();
        if (thumbs.length < 2) {
          thumbsSyncing = true;
          viewport.scrollLeft = 0;
          galleryThumbs.style.transform = '';
          thumbsSyncing = false;
          return;
        }

        const step = getThumbStep();
        if (step <= 0) return;

        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const targetLeft = Math.min(maxScroll, Math.max(0, getGalleryFractionalIndex() * step));
        thumbsSyncing = true;
        viewport.scrollLeft = targetLeft;
        galleryThumbs.style.transform = '';
        thumbsSyncing = false;
      }

      function scheduleThumbsSync() {
        if (thumbsSyncRaf != null) return;
        thumbsSyncRaf = window.requestAnimationFrame(() => {
          thumbsSyncRaf = null;
          syncThumbsToGallery();
        });
      }

      function renderVisibleThumbs(nextMediaId) {
        getThumbs().forEach((thumb) => {
          thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === String(nextMediaId));
        });
        syncThumbsToGallery();
      }

      function setActiveMedia(mediaId) {
        if (!mediaId || String(mediaId) === String(activeMediaId)) return;
        activeMediaId = mediaId;

        const galleryItems = getGalleryItems();
        const thumbPrev = wrapper.querySelector('[data-thumb-prev]');
        const thumbNext = wrapper.querySelector('[data-thumb-next]');

        galleryItems.forEach((item) => {
          const isActive = String(item.dataset.mediaId) === String(mediaId);
          item.classList.toggle('is-active', isActive);

          const video = item.querySelector('video');
          if (video) {
            if (isActive) {
              video.muted = true;
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }

          const deferredVideo = item.querySelector('deferred-media');
          if (deferredVideo) {
            if (isActive) {
              if (!deferredVideo.getAttribute('data-media-loaded') && typeof deferredVideo.showDeferredMedia === 'function') {
                deferredVideo.showDeferredMedia();
              } else if (typeof deferredVideo.playMedia === 'function') {
                deferredVideo.playMedia();
              }
            } else if (typeof deferredVideo.pauseMedia === 'function') {
              deferredVideo.pauseMedia();
            }
          }
        });

        renderVisibleThumbs(mediaId);
        if (thumbPrev && thumbNext && mediaItems.length) {
          const idx = mediaItems.findIndex((m) => String(m.id) === String(mediaId));
          const active = idx === -1 ? 0 : idx;
          thumbPrev.hidden = active <= 0;
          thumbNext.hidden = active >= mediaItems.length - 1;
        }
      }

      function updateDisplay(variant) {
        if (!variant) {
          if (cartBtn) cartBtn.disabled = true;
          if (cartBtnText) cartBtnText.textContent = unavailableText.toUpperCase();
          syncFloatingUi();
          return;
        }

        if (variantIdInput) variantIdInput.value = variant.id;
        if (priceEl) priceEl.innerHTML = variant.price_text;
        if (cartBtn) cartBtn.disabled = !variant.available;
        if (cartBtnText) cartBtnText.textContent = (variant.available ? addToCartText : soldOutText).toUpperCase();

        const quantity = Number(variant.inventory_quantity || 0);
        if (stockEl) {
          if (variant.inventory_management && quantity > 0 && quantity <= 5) {
            stockEl.hidden = false;
            stockEl.textContent = getLowStockText(quantity);
          } else {
            stockEl.hidden = true;
          }
        }

        if (selectedColorEl) {
          const checkedColor = form.querySelector('.atlas-product__swatch-input:checked');
          if (checkedColor) {
            selectedColorEl.textContent = checkedColor.value.toUpperCase();
          }
        }

        if (variant.featured_media_id) {
          updateGallery(variant.featured_media_id);
        }

        // Only update URL if we're on the actual product page, not in quick view modal
        if (variant.url && !wrapper.closest('#quick-add-modal-content')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.pathname + url.search);
        }

        syncFloatingUi();
      }

      function syncVariantState() {
        let selected = getSelectedOptions();
        let variant = findExactVariant(selected);

        if (!variant || !variant.available) {
          variant = findFirstMatchingVariant(selected);
          if (variant) {
            syncInputsToVariant(variant);
            selected = getSelectedOptions();
          }
        }

        updateOptionAvailability();
        updateDisplay(findExactVariant(selected) || variant);
      }

      function handleGalleryPointer(event) {
        if (!(event.target instanceof Element)) return;

        const thumb = event.target.closest('[data-thumb]');
        if (thumb instanceof HTMLElement && wrapper.contains(thumb)) {
          if (thumbsDidDrag) {
            thumbsDidDrag = false;
            return;
          }
          if (!thumb.dataset.mediaId) return;
          event.preventDefault();
          updateGallery(thumb.dataset.mediaId);
          return;
        }

        if (event.target.closest('[data-thumb-prev]')) {
          event.preventDefault();
          const galleryItems = getGalleryItems();
          const idx = getActiveGalleryIndex();
          if (idx > 0) updateGallery(galleryItems[idx - 1].dataset.mediaId);
          return;
        }

        if (event.target.closest('[data-thumb-next]')) {
          event.preventDefault();
          const galleryItems = getGalleryItems();
          const idx = getActiveGalleryIndex();
          if (idx < galleryItems.length - 1) updateGallery(galleryItems[idx + 1].dataset.mediaId);
        }
      }

      const handleGalleryScroll = () => {
        scheduleThumbsSync();
        if (galleryNavLock) return;

        const mediaId = getActiveMediaId();
        if (mediaId) {
          getThumbs().forEach((thumb) => {
            thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === String(mediaId));
          });
        }

        clearTimeout(galleryScrollSyncTimer);
        galleryScrollSyncTimer = window.setTimeout(syncActiveMediaFromScroll, 80);
      };

      const handleGalleryScrollEnd = () => {
        if (!galleryNavLock) syncActiveMediaFromScroll();
      };

      const THUMBS_DRAG_THRESHOLD = 10;

      function onThumbsPointerDown(event) {
        if (!galleryThumbsOuter) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        thumbsDidDrag = false;
        const thumb = event.target instanceof Element ? event.target.closest('[data-thumb]') : null;
        thumbsPointer = {
          id: event.pointerId,
          type: event.pointerType,
          x: event.clientX,
          y: event.clientY,
          scroll: galleryThumbsOuter.scrollLeft,
          captured: false,
          thumb: thumb instanceof HTMLElement ? thumb : null,
        };
      }

      function onThumbsPointerMove(event) {
        if (!thumbsPointer || event.pointerId !== thumbsPointer.id || !galleryThumbsOuter) return;
        if (thumbsPointer.type !== 'mouse') return;
        const dx = event.clientX - thumbsPointer.x;
        if (Math.abs(dx) < THUMBS_DRAG_THRESHOLD) return;
        if (!thumbsPointer.captured) {
          thumbsDidDrag = true;
          thumbsPointer.captured = true;
          galleryThumbsOuter.classList.add('is-dragging');
          try {
            galleryThumbsOuter.setPointerCapture(event.pointerId);
          } catch (error) {}
        }
        markThumbsUserScroll();
        galleryThumbsOuter.scrollLeft = thumbsPointer.scroll - dx;
        event.preventDefault();
      }

      function onThumbsPointerUp(event) {
        if (!thumbsPointer || event.pointerId !== thumbsPointer.id) return;
        const start = thumbsPointer;
        const scrolled = galleryThumbsOuter
          ? Math.abs(galleryThumbsOuter.scrollLeft - start.scroll) > THUMBS_DRAG_THRESHOLD
          : false;
        const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y) > THUMBS_DRAG_THRESHOLD;
        const wasDrag = thumbsDidDrag || scrolled || moved;
        thumbsPointer = null;
        galleryThumbsOuter?.classList.remove('is-dragging');
        if (wasDrag) {
          markThumbsUserScroll();
          return;
        }
        if (start.thumb instanceof HTMLElement && start.thumb.dataset.mediaId) {
          updateGallery(start.thumb.dataset.mediaId);
        }
      }

      wrapper.addEventListener('click', handleGalleryPointer);
      window.addEventListener('resize', scheduleThumbsSync);
      if (galleryThumbsOuter) {
        galleryThumbsOuter.addEventListener('pointerdown', onThumbsPointerDown);
        galleryThumbsOuter.addEventListener('pointermove', onThumbsPointerMove);
        galleryThumbsOuter.addEventListener('pointerup', onThumbsPointerUp);
        galleryThumbsOuter.addEventListener('pointercancel', onThumbsPointerUp);
        galleryThumbsOuter.addEventListener('scroll', markThumbsUserScroll, { passive: true });
      }
      this._atlasGalleryCleanup = () => {
        wrapper.removeEventListener('click', handleGalleryPointer);
        window.removeEventListener('resize', scheduleThumbsSync);
        if (galleryThumbsOuter) {
          galleryThumbsOuter.removeEventListener('pointerdown', onThumbsPointerDown);
          galleryThumbsOuter.removeEventListener('pointermove', onThumbsPointerMove);
          galleryThumbsOuter.removeEventListener('pointerup', onThumbsPointerUp);
          galleryThumbsOuter.removeEventListener('pointercancel', onThumbsPointerUp);
          galleryThumbsOuter.removeEventListener('scroll', markThumbsUserScroll);
        }
        if (galleryMain) {
          galleryMain.removeEventListener('scroll', handleGalleryScroll);
          galleryMain.removeEventListener('scrollend', handleGalleryScrollEnd);
        }
        clearTimeout(galleryNavLockTimer);
        clearTimeout(galleryScrollSyncTimer);
        clearTimeout(thumbsUserScrollTimer);
        if (thumbsSyncRaf != null) {
          window.cancelAnimationFrame(thumbsSyncRaf);
          thumbsSyncRaf = null;
        }
      };

      if (galleryMain) {
        galleryMain.addEventListener('scroll', handleGalleryScroll, { passive: true });
        galleryMain.addEventListener('scrollend', handleGalleryScrollEnd, { passive: true });
      }

      form.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-position]')) return;
        syncVariantState();
      });

      function setupFloatingAtc() {
        if (!cartBtn) return;
        if (wrapper.closest('#quick-add-modal-content')) return;

        const shopifySectionEl = section.closest('.shopify-section');
        const floatingWrap = shopifySectionEl && shopifySectionEl.querySelector('[data-atlas-floating-atc]');
        const floatingBtn = floatingWrap && floatingWrap.querySelector('[data-atlas-floating-atc-btn]');
        if (!floatingWrap || !floatingBtn) return;

        const sectionId = section.dataset.sectionId;
        let scrollPast = false;
        let hiddenByFooter = false;

        /** @type {IntersectionObserver | null} */
        let floatingBuyObs = null;

        /** @type {(() => void) | null} */
        let floatingContrastScrollCleanup = null;

        /** @type {number | null} */
        let contrastRaf = null;

        function updateFloatingOnDark() {
          if (floatingWrap.dataset.visible !== 'true') {
            floatingWrap.removeAttribute('data-on-dark');
            return;
          }

          const zoneTop = window.innerHeight - 140;
          let onDark = false;

          document.querySelectorAll('[data-atlas-floating-contrast-target]').forEach((el) => {
            const r = el.getBoundingClientRect();
            const overlapsVertically = r.bottom > zoneTop && r.top < window.innerHeight;
            const overlapsHorizontally = r.right > 0 && r.left < window.innerWidth;
            if (overlapsVertically && overlapsHorizontally) {
              onDark = true;
            }
          });

          if (onDark) {
            floatingWrap.setAttribute('data-on-dark', 'true');
          } else {
            floatingWrap.removeAttribute('data-on-dark');
          }
        }

        function scheduleFloatingContrastUpdate() {
          if (contrastRaf != null) cancelAnimationFrame(contrastRaf);
          contrastRaf = requestAnimationFrame(() => {
            contrastRaf = null;
            updateFloatingOnDark();
          });
        }

        const floatingOptBtns = Array.from(floatingWrap.querySelectorAll('.atlas-product__floating-opt-btn'));

        function recomputeFloatingState() {
          const cartRect = cartBtn.getBoundingClientRect();
          scrollPast = cartRect.bottom < 0;
          hiddenByFooter = false;
        }

        function applyFloatingVisibility() {
          recomputeFloatingState();
          const show = scrollPast && !hiddenByFooter && !cartBtn.disabled;
          floatingWrap.dataset.visible = show ? 'true' : 'false';
          floatingWrap.setAttribute('aria-hidden', show ? 'false' : 'true');
          scheduleFloatingContrastUpdate();

          // Sync active state of variant buttons from current selection
          const selected = getSelectedOptions();
          const currentVariant = findExactVariant(selected);
          if (currentVariant) {
            floatingOptBtns.forEach((btn) => {
              const pos = parseInt(btn.dataset.optionPosition) - 1;
              const isActive = currentVariant.options[pos] === btn.dataset.value;
              btn.dataset.active = isActive ? 'true' : 'false';
              btn.setAttribute('aria-pressed', String(isActive));
            });
          }
        }

        syncFloatingUi = applyFloatingVisibility;

        floatingBuyObs = new IntersectionObserver(() => {
          applyFloatingVisibility();
        }, { threshold: 0 });

        floatingBuyObs.observe(cartBtn);

        floatingBtn.addEventListener('click', () => {
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          if (isMobile) {
            if (cartBtn && !cartBtn.disabled) cartBtn.click();
          } else {
            const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const target = document.getElementById('AtlasProductForm-' + sectionId);
            if (target instanceof HTMLElement) {
              target.scrollIntoView({ behavior: prefersReduced ? 'instant' : 'smooth', block: 'start' });
            }
            const focusAfter = () => {
              if (cartBtn instanceof HTMLElement) cartBtn.focus({ preventScroll: true });
            };
            if (prefersReduced) {
              focusAfter();
            } else {
              window.setTimeout(focusAfter, 400);
            }
          }
        });

        // Proxy floating variant button clicks to the main form's radio inputs
        floatingWrap.addEventListener('click', (event) => {
          const btn = event.target.closest('.atlas-product__floating-opt-btn');
          if (!btn || btn.dataset.active === 'true' || btn.classList.contains('atlas-product__floating-opt-btn--unavailable')) return;

          const optionPosition = btn.dataset.optionPosition;
          const value = btn.dataset.value;
          const radios = Array.from(form.querySelectorAll(`input[data-option-position="${optionPosition}"]`));
          const radio = radios.find((r) => r.value === value);
          if (radio && !radio.disabled) radio.click();
        });

        // Clicking the price box should trigger the floating ATC button
        const floatingPriceEl = floatingWrap.querySelector('[data-atlas-floating-price]');
        if (floatingPriceEl) {
          floatingPriceEl.addEventListener('click', () => {
            if (!floatingBtn.disabled) floatingBtn.click();
          });
        }

        const onScrollOrResize = () => {
          applyFloatingVisibility();
          scheduleFloatingContrastUpdate();
        };
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize);

        floatingContrastScrollCleanup = () => {
          window.removeEventListener('scroll', onScrollOrResize);
          window.removeEventListener('resize', onScrollOrResize);
          if (contrastRaf != null) {
            cancelAnimationFrame(contrastRaf);
            contrastRaf = null;
          }
        };

        this._atlasFloatingCleanup = () => {
          floatingBuyObs?.disconnect();
          floatingContrastScrollCleanup?.();
          floatingContrastScrollCleanup = null;
        };

        applyFloatingVisibility();
      }

      setupFloatingAtc.call(section);

      function setupLightbox() {
        if (wrapper.closest('#quick-add-modal-content')) return;

        const host = wrapper.closest('section') || wrapper.parentElement;
        const dialog = host && host.querySelector('[data-atlas-lightbox]');
        if (!(dialog instanceof HTMLDialogElement)) return;

        const stage = dialog.querySelector('[data-atlas-lightbox-stage]');
        const imageEl = dialog.querySelector('[data-atlas-lightbox-image]');
        const closeBtn = dialog.querySelector('[data-atlas-lightbox-close]');
        const prevBtn = dialog.querySelector('[data-atlas-lightbox-prev]');
        const nextBtn = dialog.querySelector('[data-atlas-lightbox-next]');
        if (!(stage instanceof HTMLElement) || !(imageEl instanceof HTMLImageElement)) return;

        const TAP_MOVE = 10;
        const TAP_TIME = 500;
        let lightboxIndex = 0;
        let requestedSrc = '';
        let galleryPointer = null;
        let openedAt = 0;
        const lightboxZoom =
          typeof window.AtlasImageZoom === 'function'
            ? new window.AtlasImageZoom(stage, imageEl, {
                onSwipe: (delta) => stepLightbox(delta),
                onBackdropTap: () => closeLightbox(),
              })
            : null;
        if (lightboxZoom) lightboxZoom.attach();

        function getLightboxItems() {
          const fromJson = mediaItems.filter((item) => item && item.media_type === 'image' && item.full_url);
          if (fromJson.length) return fromJson;

          return getGalleryItems()
            .filter((item) => item.dataset.mediaType === 'image' && item.dataset.fullUrl)
            .map((item) => ({
              id: item.dataset.mediaId,
              full_url: item.dataset.fullUrl,
              full_srcset: '',
              alt: item.querySelector('img')?.alt || '',
            }));
        }

        function isLightboxOpen() {
          return dialog.open || dialog.hasAttribute('open');
        }

        function indexForMediaId(mediaId) {
          const items = getLightboxItems();
          const match = items.findIndex((item) => String(item.id) === String(mediaId));
          return match === -1 ? 0 : match;
        }

        function preloadNeighbors(index) {
          const items = getLightboxItems();
          [items[index - 1], items[index + 1]].forEach((item) => {
            if (!item?.full_url) return;
            const preload = new Image();
            preload.src = item.full_url;
            if (item.full_srcset) preload.srcset = item.full_srcset;
          });
        }

        function syncLightboxArrows(index) {
          const items = getLightboxItems();
          const hasMany = items.length > 1;
          if (prevBtn instanceof HTMLElement) prevBtn.hidden = !hasMany || index <= 0;
          if (nextBtn instanceof HTMLElement) nextBtn.hidden = !hasMany || index >= items.length - 1;
        }

        function renderLightboxImage(index) {
          const items = getLightboxItems();
          const item = items[index];
          if (!item) return;

          lightboxIndex = index;
          if (lightboxZoom) lightboxZoom.reset();
          syncLightboxArrows(index);

          const galleryImg = wrapper.querySelector(
            `.atlas-product__gallery-item[data-media-id="${item.id}"] img.atlas-product__gallery-image`
          );
          imageEl.alt = item.alt || '';
          imageEl.sizes = '100vw';

          if (galleryImg instanceof HTMLImageElement && galleryImg.currentSrc && imageEl.src !== item.full_url) {
            imageEl.removeAttribute('srcset');
            imageEl.src = galleryImg.currentSrc;
          }

          requestedSrc = item.full_url;
          const upgrade = new Image();
          upgrade.decoding = 'async';
          if (item.full_srcset) upgrade.srcset = item.full_srcset;
          upgrade.sizes = '100vw';
          upgrade.onload = () => {
            if (requestedSrc !== item.full_url) return;
            if (item.full_srcset) imageEl.srcset = item.full_srcset;
            else imageEl.removeAttribute('srcset');
            imageEl.src = item.full_url;
          };
          upgrade.onerror = () => {
            if (requestedSrc !== item.full_url) return;
            if (galleryImg instanceof HTMLImageElement && galleryImg.currentSrc) {
              imageEl.removeAttribute('srcset');
              imageEl.src = galleryImg.currentSrc;
            }
          };
          upgrade.src = item.full_url;

          preloadNeighbors(index);
        }

        function unlockPageScroll() {
          document.documentElement.classList.remove('atlas-lightbox-open');
        }

        function closeLightbox() {
          if (lightboxZoom) lightboxZoom.reset();
          if (!isLightboxOpen()) {
            unlockPageScroll();
            return;
          }
          if (typeof dialog.close === 'function') dialog.close();
          unlockPageScroll();
        }

        function openLightbox(mediaId) {
          const items = getLightboxItems();
          if (!items.length) return;

          const nextIndex = indexForMediaId(mediaId);
          if (isLightboxOpen()) {
            renderLightboxImage(nextIndex);
            return;
          }

          renderLightboxImage(nextIndex);
          document.documentElement.classList.add('atlas-lightbox-open');
          openedAt = Date.now();
          if (lightboxZoom) lightboxZoom.ignoreBackdropUntil = openedAt + 350;

          try {
            dialog.showModal();
          } catch (error) {
            dialog.setAttribute('open', '');
          }

          if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
        }

        function stepLightbox(delta) {
          const items = getLightboxItems();
          const nextIndex = lightboxIndex + delta;
          if (nextIndex < 0 || nextIndex >= items.length) return;
          renderLightboxImage(nextIndex);
          if (items[nextIndex].id) updateGallery(items[nextIndex].id);
        }

        function isGalleryControl(target) {
          return Boolean(
            target.closest('[data-thumb], [data-thumb-prev], [data-thumb-next], .atlas-product__gallery-arrow, a, button')
          );
        }

        function onGalleryPointerDown(event) {
          if (isLightboxOpen()) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (!(event.target instanceof Element) || isGalleryControl(event.target)) {
            galleryPointer = null;
            return;
          }

          const item = event.target.closest('.atlas-product__gallery-item');
          if (!(item instanceof HTMLElement) || item.dataset.mediaType !== 'image') {
            galleryPointer = null;
            return;
          }

          galleryPointer = {
            x: event.clientX,
            y: event.clientY,
            scroll: galleryMain ? galleryMain.scrollLeft : 0,
            time: Date.now(),
            mediaId: item.dataset.mediaId,
          };
        }

        function onGalleryPointerEnd(event) {
          const start = galleryPointer;
          galleryPointer = null;
          if (!start || isLightboxOpen()) return;
          if (Date.now() - start.time > TAP_TIME) return;
          if (Math.abs(event.clientX - start.x) > TAP_MOVE) return;
          if (Math.abs(event.clientY - start.y) > TAP_MOVE) return;
          if (galleryMain && Math.abs(galleryMain.scrollLeft - start.scroll) > TAP_MOVE) return;
          event.preventDefault();
          openLightbox(start.mediaId);
        }

        function onLightboxKeyDown(event) {
          if (!isLightboxOpen()) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            closeLightbox();
            return;
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            stepLightbox(-1);
            return;
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            stepLightbox(1);
          }
        }

        function onLightboxClick(event) {
          if (!(event.target instanceof Element)) return;
          if (event.target.closest('[data-atlas-lightbox-close]')) {
            event.preventDefault();
            closeLightbox();
            return;
          }
          if (event.target.closest('[data-atlas-lightbox-prev]')) {
            event.preventDefault();
            stepLightbox(-1);
            return;
          }
          if (event.target.closest('[data-atlas-lightbox-next]')) {
            event.preventDefault();
            stepLightbox(1);
          }
        }

        function onDialogClose() {
          unlockPageScroll();
        }

        const galleryRoot = wrapper.querySelector('.atlas-product__gallery');
        if (galleryRoot) {
          galleryRoot.addEventListener('pointerdown', onGalleryPointerDown);
          galleryRoot.addEventListener('pointerup', onGalleryPointerEnd);
          galleryRoot.addEventListener('pointercancel', () => {
            galleryPointer = null;
          });
        }

        dialog.addEventListener('click', onLightboxClick);
        dialog.addEventListener('keydown', onLightboxKeyDown);
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeLightbox();
        });
        dialog.addEventListener('close', onDialogClose);

        this._atlasLightboxCleanup = () => {
          closeLightbox();
          if (lightboxZoom) lightboxZoom.detach();
          if (galleryRoot) {
            galleryRoot.removeEventListener('pointerdown', onGalleryPointerDown);
            galleryRoot.removeEventListener('pointerup', onGalleryPointerEnd);
          }
          dialog.removeEventListener('click', onLightboxClick);
          dialog.removeEventListener('keydown', onLightboxKeyDown);
          dialog.removeEventListener('close', onDialogClose);
        };
      }

      setupLightbox.call(section);

      const initialGalleryItems = getGalleryItems();
      const initialMediaId = initialGalleryItems[0]?.dataset.mediaId;
      if (initialMediaId) setActiveMedia(initialMediaId);
      window.requestAnimationFrame(syncThumbsToGallery);
      const thumbPrev = wrapper.querySelector('[data-thumb-prev]');
      const thumbNext = wrapper.querySelector('[data-thumb-next]');
      if (thumbPrev && thumbNext && mediaItems.length) {
        thumbPrev.hidden = true;
        thumbNext.hidden = mediaItems.length <= 1;
      }
      syncVariantState();

      const sizeChartBtn = section.querySelector('[data-open-size-chart]');
      const drawerId = sizeChartBtn?.getAttribute('aria-controls');
      const quickAddDialog = wrapper.closest('.quick-add-modal');
      const gridRoot = section.closest('[data-product-grid-content]');

      function resolveSizeChartDrawer() {
        if (!drawerId) return null;

        if (gridRoot instanceof HTMLElement) {
          const scopedDrawer = gridRoot.querySelector(`#${CSS.escape(drawerId)}`);
          if (scopedDrawer instanceof HTMLElement) return scopedDrawer;
        }

        const globalDrawer = document.getElementById(drawerId);
        return globalDrawer instanceof HTMLElement ? globalDrawer : null;
      }

      function restoreDrawerHome(drawer, home) {
        if (!(drawer instanceof HTMLElement) || !(home instanceof HTMLElement)) return;
        if (drawer.parentElement !== home) {
          home.appendChild(drawer);
        }
      }

      function mountQuickAddSizeChartDrawer(drawer) {
        if (!(quickAddDialog instanceof HTMLElement) || !(drawer instanceof HTMLElement)) return;

        drawer.classList.add('atlas-size-chart-drawer--quick-add');
        if (drawer.parentElement !== quickAddDialog) {
          quickAddDialog.appendChild(drawer);
        }
      }

      function setupSizeChartDrawer(attempt = 0) {
        const sizeChartDrawer = resolveSizeChartDrawer();
        const maxAttempts = quickAddDialog instanceof HTMLElement ? 12 : 4;

        if (!(sizeChartBtn instanceof HTMLElement) || !(sizeChartDrawer instanceof HTMLElement)) {
          if (sizeChartBtn instanceof HTMLElement && drawerId && attempt < maxAttempts) {
            window.requestAnimationFrame(() => setupSizeChartDrawer(attempt + 1));
          }
          return;
        }

        const sizeChartDrawerHome = sizeChartDrawer.parentElement;
        mountQuickAddSizeChartDrawer(sizeChartDrawer);

        const closeEls = sizeChartDrawer.querySelectorAll('[data-close-size-chart]');
        let focusBeforeOpen = null;

        function placeSizeChartScrollHint() {
          const hint = sizeChartDrawer.querySelector('[data-size-chart-scroll-hint]');
          const rte = sizeChartDrawer.querySelector('.atlas-size-chart-drawer__rte');
          const wrap = rte?.querySelector('.rte-table-wrapper');
          if (hint && wrap && hint.previousElementSibling !== wrap) {
            wrap.insertAdjacentElement('afterend', hint);
          }
          if (hint && wrap) hint.removeAttribute('hidden');
        }

        placeSizeChartScrollHint();
        window.requestAnimationFrame(() => {
          placeSizeChartScrollHint();
          window.setTimeout(placeSizeChartScrollHint, 60);
        });

        function openSizeChartDrawer() {
          focusBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          mountQuickAddSizeChartDrawer(sizeChartDrawer);
          sizeChartDrawer.hidden = false;
          sizeChartBtn.setAttribute('aria-expanded', 'true');
          document.body.classList.add('atlas-size-chart-drawer-open');
          placeSizeChartScrollHint();
          requestAnimationFrame(() => {
            sizeChartDrawer.classList.add('is-open');
          });
          const closeBtn = sizeChartDrawer.querySelector('.atlas-size-chart-drawer__close');
          if (closeBtn instanceof HTMLElement) closeBtn.focus();
        }

        function closeSizeChartDrawer() {
          sizeChartBtn.setAttribute('aria-expanded', 'false');
          sizeChartDrawer.classList.remove('is-open');
          document.body.classList.remove('atlas-size-chart-drawer-open');
          window.setTimeout(() => {
            sizeChartDrawer.hidden = true;
            if (focusBeforeOpen) focusBeforeOpen.focus();
          }, 280);
        }

        const onSizeChartOpen = (event) => {
          event.preventDefault();
          openSizeChartDrawer();
        };

        const onSizeChartClose = () => closeSizeChartDrawer();

        const onSizeChartKeyDown = (event) => {
          if (event.key !== 'Escape' || !sizeChartDrawer.classList.contains('is-open')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          closeSizeChartDrawer();
        };

        sizeChartBtn.addEventListener('click', onSizeChartOpen);
        closeEls.forEach((el) => {
          el.addEventListener('click', onSizeChartClose);
        });
        document.addEventListener('keydown', onSizeChartKeyDown, true);

        section._atlasSizeChartCleanup = () => {
          sizeChartBtn.removeEventListener('click', onSizeChartOpen);
          closeEls.forEach((el) => {
            el.removeEventListener('click', onSizeChartClose);
          });
          document.removeEventListener('keydown', onSizeChartKeyDown, true);

          if (sizeChartDrawer.classList.contains('is-open')) {
            closeSizeChartDrawer();
          }

          sizeChartDrawer.classList.remove('atlas-size-chart-drawer--quick-add');
          restoreDrawerHome(sizeChartDrawer, sizeChartDrawerHome);
        };
      }

      setupSizeChartDrawer();

      function resolveDescDrawer(descDrawerId) {
        if (!descDrawerId) return null;

        if (gridRoot instanceof HTMLElement) {
          const scopedDrawer = gridRoot.querySelector(`#${CSS.escape(descDrawerId)}`);
          if (scopedDrawer instanceof HTMLElement) return scopedDrawer;
        }

        const globalDrawer = document.getElementById(descDrawerId);
        return globalDrawer instanceof HTMLElement ? globalDrawer : null;
      }

      function mountDescDrawer(drawer) {
        if (!(drawer instanceof HTMLElement)) return;

        if (quickAddDialog instanceof HTMLElement) {
          drawer.classList.add('atlas-desc-drawer--quick-add');
          if (drawer.parentElement !== quickAddDialog) {
            quickAddDialog.appendChild(drawer);
          }
          return;
        }

        drawer.classList.remove('atlas-desc-drawer--quick-add');
        if (drawer.parentElement !== document.body) {
          document.body.appendChild(drawer);
        }
      }

      function setupDescDrawer(attempt = 0) {
        const descDrawerBtn = section.querySelector('[data-open-desc-drawer]');
        if (!(descDrawerBtn instanceof HTMLElement)) return;

        const descDrawerId = descDrawerBtn.getAttribute('aria-controls');
        const descDrawer = resolveDescDrawer(descDrawerId);
        const maxAttempts = quickAddDialog instanceof HTMLElement ? 12 : 4;

        if (!(descDrawer instanceof HTMLElement)) {
          if (descDrawerId && attempt < maxAttempts) {
            window.requestAnimationFrame(() => setupDescDrawer(attempt + 1));
          }
          return;
        }

        const descDrawerHome = descDrawer.parentElement;
        const descCloseEls = descDrawer.querySelectorAll('[data-close-desc-drawer]');
        let descFocusBeforeOpen = null;

        function openDescDrawer() {
          descFocusBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          mountDescDrawer(descDrawer);
          descDrawer.hidden = false;
          descDrawerBtn.setAttribute('aria-expanded', 'true');
          document.body.classList.add('atlas-desc-drawer-open');
          requestAnimationFrame(() => {
            descDrawer.classList.add('is-open');
          });
          const closeBtn = descDrawer.querySelector('.atlas-desc-drawer__close');
          if (closeBtn instanceof HTMLElement) closeBtn.focus();
        }

        function closeDescDrawer() {
          descDrawerBtn.setAttribute('aria-expanded', 'false');
          descDrawer.classList.remove('is-open');
          document.body.classList.remove('atlas-desc-drawer-open');
          window.setTimeout(() => {
            descDrawer.hidden = true;
            if (descFocusBeforeOpen) descFocusBeforeOpen.focus();
          }, 280);
        }

        const onDescDrawerOpen = (event) => {
          event.preventDefault();
          openDescDrawer();
        };

        const onDescDrawerClose = () => closeDescDrawer();

        const onDescDrawerKeyDown = (event) => {
          if (event.key !== 'Escape' || !descDrawer.classList.contains('is-open')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          closeDescDrawer();
        };

        descDrawerBtn.addEventListener('click', onDescDrawerOpen);
        descCloseEls.forEach((el) => {
          el.addEventListener('click', onDescDrawerClose);
        });
        document.addEventListener('keydown', onDescDrawerKeyDown, true);

        section._atlasDescDrawerCleanup = () => {
          descDrawerBtn.removeEventListener('click', onDescDrawerOpen);
          descCloseEls.forEach((el) => {
            el.removeEventListener('click', onDescDrawerClose);
          });
          document.removeEventListener('keydown', onDescDrawerKeyDown, true);

          if (descDrawer.classList.contains('is-open')) {
            closeDescDrawer();
          }

          descDrawer.classList.remove('atlas-desc-drawer--quick-add');
          restoreDrawerHome(descDrawer, descDrawerHome);
        };
      }

      setupDescDrawer();
    }

    disconnectedCallback() {
      if (typeof this._atlasDescDrawerCleanup === 'function') {
        this._atlasDescDrawerCleanup();
        this._atlasDescDrawerCleanup = null;
      }
      if (typeof this._atlasSizeChartCleanup === 'function') {
        this._atlasSizeChartCleanup();
        this._atlasSizeChartCleanup = null;
      }
      if (typeof this._atlasGalleryCleanup === 'function') {
        this._atlasGalleryCleanup();
        this._atlasGalleryCleanup = null;
      }
      if (typeof this._atlasGalleryObserverCleanup === 'function') {
        this._atlasGalleryObserverCleanup();
        this._atlasGalleryObserverCleanup = null;
      }
      if (typeof this._atlasFloatingCleanup === 'function') {
        this._atlasFloatingCleanup();
        this._atlasFloatingCleanup = null;
      }
      if (typeof this._atlasLightboxCleanup === 'function') {
        this._atlasLightboxCleanup();
        this._atlasLightboxCleanup = null;
      }
    }
  });
}
